import React, { useContext, useEffect, useState } from 'react';
import Logo from "../../../../../assets/images/LogoNew.svg";
import LanguagePicker from '../../../../../components/LanguagePicker/LanguagePicker';
import { REVIEW_TASK_KIND } from '../../../../../constants/reviewConstants';
import { useReviewNavigationContext } from '../../../../../components/ReviewWizard/useReviewNavigation';
import { DialogContext } from '../../../../../store/context/DialogContext';
import { useTranslation } from '../../../../../store/context/TranslationContext';
import { doGET } from '../../../../../util/HttpUtil';
import ButtonGroupNew from '../../BusinessLiteForms/components/ButtonGroupNew';
import ProofOfDocuments from '../../BusinessLiteForms/components/ProofOfDocument';
import { FormEndpoints, FormEndpointsForEdd } from '../../ExtraQuestions';
import { buildAutomationChildTestId, normalizeAutomationTestIdSegment } from '../../../../../components/InputField/testId';

const DOCUMENT_REDO_TEST_ID = "individual-plus-document-redo";

const ApplicationDocRedo = ({ hideContainerStyle, type, formType, formId, asLabel, recordId, isForEDD }) => {
    const { t } = useTranslation();
    const [reqdApplicationDocs, setReqdApplicationDocs] = useState([])
    const [redoListLoading, setRedoListLoading] = useState(false)
    const [uboLoading, setUboLoading] = useState(false)
    const [documentTypesLoading, setDocumentTypesLoading] = useState(false)
    const [submitLoading, setSubmitLoading] = useState(false)
    const [documentTypes, setDocumentTypes] = useState([])
    const [documentErrors, setDocumentErrors] = useState({})
    const { showMessage, showError } = useContext(DialogContext)
    const [uboDetail, setUboDetail] = useState(null);
    const reviewNavigation = useReviewNavigationContext();

    const loading = redoListLoading || uboLoading || documentTypesLoading || submitLoading;
    const isPageReady = !redoListLoading && !uboLoading && !documentTypesLoading;

    const fetchUBOs = async () => {
        setUboLoading(true);
        try {
            const uboResponse = await doGET(`/api/ubo/grid?application_id=${recordId}&rows=-1`);
            const obj = {};
            (uboResponse?.data?.rows ?? []).forEach((v) => {
                obj[v?._id] = v;
            });
            setUboDetail(obj);
        } catch (error) {
            console.error('Error fetching ubo details:', error);

        } finally {
            setUboLoading(false);
        }
    }


    const fetch = async () => {
        setRedoListLoading(true)
        try {
            const response = await doGET(`/api/application-doc/redo/list?form_id=${formId}`)
            if (response?.status == 200) {
                setReqdApplicationDocs(response?.data)
            }
        } catch (error) {
            showError(error)
        } finally {
            setRedoListLoading(false)
        }
    }


    const fetchDocumentTypes = async () => {
        setDocumentTypesLoading(true);
        try {
            const docTypeResponse = await doGET(`/api/doc-type/grid?rows=-1`);
            if (docTypeResponse?.status == 200) {
                setDocumentTypes(docTypeResponse?.data?.rows.reduce((acc, obj) => {
                    if (obj?.key && !acc[obj?.key]) {
                        acc[obj?.key] = obj?.name;
                    } else if (obj?._id && !acc[obj?._id]) {
                        acc[obj?._id] = obj?.name;
                    }
                    return acc;
                }, {}))


            }
        } catch (error) {
            showError(error)
        } finally {
            setDocumentTypesLoading(false);
        }
    }


    useEffect(() => {
        fetch()
        fetchUBOs();
        fetchDocumentTypes();
    }, [recordId])

    useEffect(() => {
        setDocumentErrors({})
    }, [reqdApplicationDocs])

    const getDocKey = (doc) => doc?._id ?? `${doc?.type ?? "unknown"}__${doc?.seq ?? "na"}__${doc?.ubo_id ?? "na"}`;

    const getDocLabel = (doc) => documentTypes?.[doc?.type] ?? doc?.type ?? "document";

    const getDocTestId = (doc, prefix = "document") => {
        const typeKey = normalizeAutomationTestIdSegment(doc?.type || "document");
        const seqKey = doc?.seq !== undefined && doc?.seq !== null && doc?.seq !== ""
            ? `seq-${normalizeAutomationTestIdSegment(doc.seq)}`
            : "single";
        return buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, `${prefix}-${typeKey}-${seqKey}`);
    };

    const getUboName = (doc) => {
        if (!doc?.ubo_id) return "";
        const ubo = uboDetail?.[doc?.ubo_id];
        const fullName = `${ubo?.firstName ?? ""} ${ubo?.lastName ?? ""}`.trim();
        return fullName ? ` for ${fullName}` : " for UBO";
    };

    const checkIsDocumentUploaded = async (doc) => {
        const applicationId = recordId || formId;
        if (!applicationId) return false;

        const params = [`rows=-1`, `form_id=${applicationId}`];
        if (doc?.ubo_id) params.push(`ubo_id=${doc?.ubo_id}`);
        if (doc?.seq !== undefined && doc?.seq !== null && doc?.seq !== "") params.push(`seq=${doc?.seq}`);
        if (doc?.type) params.push(`type=${doc?.type}`);

        const response = await doGET(`/api/application-doc/grid?${params.join("&")}`);
        if (response?.status === 200) {
            const rows = response?.data?.rows ?? [];
            return rows.some((row) => row?.attachment?.urls?.length);
        }
        return false;
    };

    const validateDocuments = async () => {
        if (!reqdApplicationDocs?.length) return true;

        const grouped = groupDocumentsByUbo(reqdApplicationDocs);
        const uboKeys = Object.keys(grouped).filter((key) => key !== "reqdApplicationDocs");
        const orderedDocs = [
            ...(grouped?.reqdApplicationDocs ?? []),
            ...uboKeys.flatMap((key) => grouped?.[key] ?? []),
        ];

        const errors = {};
        let firstErrorMessage = null;

        const results = await Promise.all(
            orderedDocs.map(async (doc) => {
                try {
                    const isUploaded = await checkIsDocumentUploaded(doc);
                    return { doc, isUploaded, error: null };
                } catch (error) {
                    return { doc, isUploaded: false, error };
                }
            })
        );

        for (const result of results) {
            const docKey = getDocKey(result.doc);
            if (result.error || !result.isUploaded) {
                const errorMessage = result.error
                    ? `Error checking ${getDocLabel(result.doc)}${getUboName(result.doc)}.`
                    : `Please upload ${getDocLabel(result.doc)}${getUboName(result.doc)}.`;
                errors[docKey] = errorMessage;
                if (!firstErrorMessage) firstErrorMessage = errorMessage;
            }
        }

        setDocumentErrors(errors);
        if (firstErrorMessage) {
            showError(firstErrorMessage);
        }
        return !firstErrorMessage;
    };

    const handleSubmit = async () => {
        if (loading) return;
        setSubmitLoading(true)
        const isValid = await validateDocuments();
        if (!isValid) {
            setSubmitLoading(false)
            return;
        }
        try {
            const submitResponse = await doGET(`/api/${isForEDD ? FormEndpointsForEdd[formType] : FormEndpoints[formType]}/submit?id=${formId}&markDocSubmit=true`)
            if (submitResponse?.status == 200) {
                await reviewNavigation?.markTasksRespondedByKind?.(REVIEW_TASK_KIND.DOCUMENT_REQUEST, { quiet: true });
                showMessage("Submitted")
                await new Promise(resolve => setTimeout(resolve, 3000));
                window.location.reload();
            }
        } catch (error) {
            showError(error)
        } finally {
            setSubmitLoading(false)
        }
    }

    const groupDocumentsByUbo = (documents) => {
        const grouped = { reqdApplicationDocs: [] };

        documents.forEach(doc => {
            if (doc?.ubo_id) {
                if (!grouped[doc?.ubo_id]) {
                    grouped[doc?.ubo_id] = [];
                }
                grouped[doc?.ubo_id].push(doc);
            } else {
                grouped?.reqdApplicationDocs.push(doc);
            }
        });

        return grouped;
    };

    const groupedDocuments = groupDocumentsByUbo(reqdApplicationDocs);

    return (
        <div style={hideContainerStyle ? {} : {}} className={hideContainerStyle ? "" : 'form-content-container xl'} data-testid={DOCUMENT_REDO_TEST_ID} data-record-id={recordId || formId}>
            {
                hideContainerStyle ? <></> : <div className='form-content-header' data-testid={buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, "header")}>
                    <img
                        src={Logo}
                        alt={t("fv-bank")}
                        className="logo logo-dark form-content-logo"
                        data-testid={buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, "logo")}
                    />
                    <div className='' data-testid={buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, "language-wrap")}><LanguagePicker testId={buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, "language")} /></div>
                </div>
            }
            <div className={` ${hideContainerStyle ? "w-100" : "form-content-body"}`} data-testid={buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, "body")}>

                {hideContainerStyle ? <></> : <div className='form-content-body-header' data-testid={buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, "body-header")}>
                    <div className='heading' data-testid={buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, "title")}>{t(type)}</div>
                </div>}

                <div className={` ${hideContainerStyle ? "d-flex flex-column justify-content-center align-items-center " : " form-content-body-content "}`} data-testid={buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, "content")}>
                    {hideContainerStyle ? <></> : <div style={{ margin: "0px 0px 30px 0px", fontSize: "16px", fontWeight: "500" }} data-testid={buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, "instructions")}>To continue with your application we require following documents.</div>}

                    <div className='w-100' data-testid={buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, "document-groups")}>
                        {groupedDocuments.reqdApplicationDocs?.length ? <>
                            <div className='mb-2' data-testid={buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, "application-documents-title")}><b>Business Application Documents</b></div>
                            <div className="fv_application_document-group" data-testid={buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, "application-documents")}>
                                {groupedDocuments.reqdApplicationDocs.map((reqdApplicationDoc) => (
                                    <ProofOfDocuments
                                        dataTestId={getDocTestId(reqdApplicationDoc, "application-document")}
                                        dataRecordId={reqdApplicationDoc?._id}
                                        isAskForInfo={true}
                                        recordId={recordId}
                                        asLabel={asLabel}
                                        remark={reqdApplicationDoc?.remark}
                                        seq={reqdApplicationDoc?.seq}
                                        single={["Business_Proof_Address", "Business_Certificate_Incorporation", "ProofOfAddress"]?.includes(reqdApplicationDoc?.type)}
                                        supportedFiles={["Business_Proof_Address", "Business_Certificate_Incorporation", "ProofOfAddress"]?.includes(reqdApplicationDoc?.type) ? ["image", "pdf"] : ["image", "pdf"]}
                                        label={documentTypes?.[reqdApplicationDoc?.type] ?? reqdApplicationDoc?.type}
                                        field={documentTypes?.[reqdApplicationDoc?.type] ?? reqdApplicationDoc?.type}
                                        showCategory={documentTypes?.[reqdApplicationDoc?.type] ? reqdApplicationDoc?.type : false}
                                        type={reqdApplicationDoc?.type} //
                                        ubo_id={reqdApplicationDoc?.ubo_id} // 
                                        error={documentErrors[getDocKey(reqdApplicationDoc)]}
                                    />
                                ))}


                            </div>    </> : null}

                        {Object.keys(groupedDocuments)
                            .filter(key => key !== 'reqdApplicationDocs')
                            .map((ubo_id) => (
                                <div key={ubo_id} data-testid={buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, "ubo-documents")} data-record-id={ubo_id}>
                                    <div className='mb-2' data-testid={buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, "ubo-documents-title")}>
                                        <b>Application Documents For {uboDetail?.[ubo_id]?.firstName ?? ""} {uboDetail?.[ubo_id]?.lastName ?? ""}</b>
                                    </div>
                                    {groupedDocuments[ubo_id].map((reqdApplicationDoc) => (
                                        <div key={getDocKey(reqdApplicationDoc)} className="w-100" data-testid={getDocTestId(reqdApplicationDoc, "ubo-document-wrap")} data-record-id={reqdApplicationDoc?._id}>
                                            <div className="fv_application_document-group w-100" data-testid={buildAutomationChildTestId(getDocTestId(reqdApplicationDoc, "ubo-document-wrap"), "group")}>
                                                <ProofOfDocuments
                                                    dataTestId={getDocTestId(reqdApplicationDoc, "ubo-document")}
                                                    dataRecordId={reqdApplicationDoc?._id}
                                                    isAskForInfo={true}
                                                    recordId={recordId}
                                                    asLabel={asLabel}
                                                    remark={reqdApplicationDoc?.remark}
                                                    seq={reqdApplicationDoc?.seq}
                                                    single={["Business_Proof_Address", "Business_Certificate_Incorporation", "ProofOfAddress"]?.includes(reqdApplicationDoc?.type)}
                                                    supportedFiles={["Business_Proof_Address", "Business_Certificate_Incorporation", "ProofOfAddress"]?.includes(reqdApplicationDoc?.type) ? ["image", "pdf"] : ["image", "pdf"]}
                                                    label={documentTypes?.[reqdApplicationDoc?.type] ?? reqdApplicationDoc?.type}
                                                    field={documentTypes?.[reqdApplicationDoc?.type] ?? reqdApplicationDoc?.type}
                                                    showCategory={documentTypes?.[reqdApplicationDoc?.type] ? reqdApplicationDoc?.type : false}
                                                    type={reqdApplicationDoc?.type}
                                                    ubo_id={reqdApplicationDoc?.ubo_id}
                                                    error={documentErrors[getDocKey(reqdApplicationDoc)]}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}

                    </div>

                    <div className=' w-100 mt-4' data-testid={buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, "footer")}>
                        <ButtonGroupNew
                            testId={buildAutomationChildTestId(DOCUMENT_REDO_TEST_ID, "navigation")}
                            primaryActionTestIdSuffix="submit-documents"
                            onNextClick={handleSubmit}
                            asLabel={false}
                            loading={loading}
                            isReadyForReviewAction={isPageReady}
                            reviewPendingReason="Please wait while document requests finish loading."
                            hideBackButton
                        />
                    </div>

                </div>
            </div>
        </div>
    )
}

export default ApplicationDocRedo
